// URL-synced search query + sort. Reading/writing through useSearchParams
// keeps the back button and shared links honest.

import { useSearchParams } from "react-router-dom";

import type { SystemEnriched, SystemSummary } from "@/types/api";

export type SortKey = "name" | "pws_id" | "tenant" | "alerts";

const VALID_SORTS: readonly SortKey[] = ["name", "pws_id", "tenant", "alerts"];

function isSortKey(v: string | null): v is SortKey {
  return v !== null && (VALID_SORTS as readonly string[]).includes(v);
}

interface ListParams {
  q: string;
  sort: SortKey;
  setQ: (next: string) => void;
  setSort: (next: SortKey) => void;
}

export function useListParams(defaultSort: SortKey = "name"): ListParams {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const sortRaw = params.get("sort");
  const sort: SortKey = isSortKey(sortRaw) ? sortRaw : defaultSort;

  function update(next: { q?: string; sort?: SortKey }): void {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next.q !== undefined) {
          if (next.q) out.set("q", next.q);
          else out.delete("q");
        }
        if (next.sort !== undefined) {
          if (next.sort === defaultSort) out.delete("sort");
          else out.set("sort", next.sort);
        }
        return out;
      },
      { replace: true },
    );
  }

  return {
    q,
    sort,
    setQ: (next) => update({ q: next }),
    setSort: (next) => update({ sort: next }),
  };
}

export function compareBySort(
  sort: SortKey,
  a: SystemSummary,
  b: SystemSummary,
  byPwsId: Map<string, SystemEnriched>,
): number {
  switch (sort) {
    case "pws_id":
      return a.pws_id.localeCompare(b.pws_id);
    case "tenant":
      return (a.tenant_name ?? "").localeCompare(b.tenant_name ?? "")
        || a.pws_name.localeCompare(b.pws_name);
    case "alerts": {
      const ea = byPwsId.get(a.pws_id);
      const eb = byPwsId.get(b.pws_id);
      // High desc, then medium desc, then low desc, then name asc.
      const ha = ea?.alert_counts.high ?? 0;
      const hb = eb?.alert_counts.high ?? 0;
      if (ha !== hb) return hb - ha;
      const ma = ea?.alert_counts.medium ?? 0;
      const mb = eb?.alert_counts.medium ?? 0;
      if (ma !== mb) return mb - ma;
      const la = ea?.alert_counts.low ?? 0;
      const lb = eb?.alert_counts.low ?? 0;
      if (la !== lb) return lb - la;
      return a.pws_name.localeCompare(b.pws_name);
    }
    case "name":
    default:
      return a.pws_name.localeCompare(b.pws_name);
  }
}
